import React, { type ComponentType } from 'react';

import { type IntlShape } from 'react-intl';
import Loadable from 'react-loadable';

import {
	DefaultExtensionProvider,
	type ExtensionParams,
	type Parameters,
} from '@atlaskit/editor-common/extensions';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import {
	ButtonStyle,
	CommonLayout,
	CornerStyle,
	type CustomSitesExtensionProviderProps,
	type ExtensionMode,
	getExtensionManifest,
	Height,
	type HubDefaultParameters,
	type ProductDependenciesProviderProps,
	SPOTLIGHT_EXTENSION_TYPE,
} from '@atlassian/company-hub-common';

import { SPOTLIGHT_EXTENSION_KEY } from '../../constants';
import type { SpotlightParameters as SpotlightParametersType } from '../../types';

import { i18n } from './messages';

const SpotlightExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_spotlight-extension" */ '../spotlight-hub-element/spotlight-extension/spotlight'
			)
		).Spotlight,
	loading: () => <UFOLoadHold name="spotlight-extension-loader"></UFOLoadHold>,
});

export const spotlightParameters = ({
	intl,
	defaultParameters,
}: {
	defaultParameters?: HubDefaultParameters;
	intl: IntlShape;
}): SpotlightParametersType => ({
	extensionTitle: intl.formatMessage(i18n.title),
	height: Height.MEDIUM,
	backgroundColor:
		defaultParameters?.backgroundColor ?? token('color.background.accent.orange.subtlest'),
	cornerStyle: CornerStyle.ROUNDED,
	layout: CommonLayout.SPLIT_IMAGE_RIGHT,
	content: {
		text: {
			title: intl.formatMessage(i18n.initialTitleParameter),
			description: intl.formatMessage(i18n.initialDescriptionParameter),
			textColor: defaultParameters?.textColor ?? token('color.text'),
		},
		button: {
			isButtonShown: false,
			text: '',
			link: '',
			style: ButtonStyle.DEFAULT,
		},
		image: {
			imageSrc:
				'https://images.unsplash.com/photo-1527519135413-1e146b552e10?q=80&w=2940&auto=format',
			altText: intl.formatMessage(i18n.initialImageAltTextParameter),
		},
	},
});

export type spotlightExtensionProviderWrapperProps = {
	contentId: string;
	defaultParameters?: HubDefaultParameters;
	mode: ExtensionMode;
	propsProvider: ComponentType<ProductDependenciesProviderProps>;
};

export const spotlightExtensionProvider = async ({
	contentId,
	propsProvider,
	defaultParameters,
}: spotlightExtensionProviderWrapperProps): Promise<
	({
		editorActions,
		openConfigPanel,
		setUpsellModalSource,
		intl,
	}: CustomSitesExtensionProviderProps) => Promise<DefaultExtensionProvider<Parameters>>
> => {
	return async ({
		editorActions,
		openConfigPanel,
		setUpsellModalSource,
		intl,
	}: CustomSitesExtensionProviderProps) => {
		const title = intl.formatMessage(i18n.title);
		const description = intl.formatMessage(i18n.description);
		const type = SPOTLIGHT_EXTENSION_TYPE;
		const render =
			async () =>
			({ node }: { node: ExtensionParams<SpotlightParametersType> }) => (
				<SpotlightExtension
					isInEditor
					contentId={contentId}
					extensionNode={node}
					PropsProvider={propsProvider}
				/>
			);

		const icons = {
			// eslint-disable-next-line import/dynamic-import-chunkname
			'48': () =>
				import(
					/* webpackChunkName: "@atlaskit-internal_atlaskit-icon-core-premium" */ '@atlaskit/icon/core/premium'
				).then(({ default: PremiumIcon }) => () => (
					<PremiumIcon color={token('color.icon.subtle')} label="" spacing="spacious" />
				)),
		};

		const parameters = spotlightParameters({ intl, defaultParameters });

		const update = async () => {
			const selectedNode = editorActions?.getSelectedNode()?.toJSON();
			const selectedLocalId = selectedNode?.attrs?.localId;

			// missing openConfigPanel and editorActions
			if (!!selectedLocalId && openConfigPanel) {
				openConfigPanel(selectedLocalId);
			}
		};

		return new DefaultExtensionProvider([
			getExtensionManifest<SpotlightParametersType>({
				key: SPOTLIGHT_EXTENSION_KEY,
				icons,
				parameters,
				update,
				render,
				title,
				type,
				description,
				setUpsellModalSource,
				source: 'premiumSpotlightInsert',
			}),
		]);
	};
};
